import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { backendProvider } from '../services/backend';
import { IUser } from '../services/backend/interfaces/IAuthService';
import { preferencesService } from '../services/preferences.service';
import BrainiacsLoader from '../components/atoms/BrainiacsLoader';

interface AuthContextType {
  currentUser: IUser | null;
  isAdmin: boolean;       // alias for isSuperAdmin (backwards compat)
  isSuperAdmin: boolean;  // labels.includes('superadmin') — global Brainiacs admin
  isTeamAdmin: boolean;   // labels.includes('teamadmin') — manages their team's campaigns
  targetEmail: string | null;
  setTargetEmail: (email: string | null) => void;
  loading: boolean;
  signInWithGoogle: () => Promise<IUser>;
  signInWithLinkedIn: () => Promise<IUser>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<IUser | null>(null);
  const [targetEmail, setTargetEmailState] = useState<string | null>(backendProvider.auth.getTargetEmail());
  const [loading, setLoading] = useState(true);

  // ---- Connection-resilient auth ----
  // Keep a ref to the last known valid user so a brief auth null event
  // (e.g. network hiccup) doesn't instantly clear the UI state.
  // We only clear currentUser when:
  //   1. The user explicitly signs out (signOut action completes).
  //   2. 30 seconds pass without the user resolving (sustained dropout).
  const lastValidUserRef = useRef<IUser | null>(null);
  const revalidationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSigningOutRef = useRef(false);
  // Recovery loop for dropout-cleared sessions: the Appwrite SDK emits no auth
  // event when connectivity RETURNS, so without this the user would sit on the
  // login screen until a manual refresh. While a dropout-clear is in effect we
  // poll the session briefly (plus immediately on `window online`), and stop
  // the moment the user resolves.
  const recoveryPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearRevalidationTimer = useCallback(() => {
    if (revalidationTimerRef.current !== null) {
      clearTimeout(revalidationTimerRef.current);
      revalidationTimerRef.current = null;
    }
  }, []);

  const stopRecoveryPoll = useCallback(() => {
    if (recoveryPollRef.current !== null) {
      clearInterval(recoveryPollRef.current);
      recoveryPollRef.current = null;
    }
  }, []);

  // One manual session probe. Safe to call repeatedly — a resolved session
  // re-fires the auth listener (self-heal); unreachable networks are a no-op.
  const probeSession = useCallback(() => {
    void backendProvider.auth.revalidateSession().catch(() => {
      /* unreachable — the poll will try again */
    });
  }, []);

  // Fires the probe the moment the browser reports connectivity back.
  useEffect(() => {
    const onOnline = () => probeSession();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [probeSession]);

  // Set target email (impersonation)
  const setTargetEmail = (email: string | null) => {
    backendProvider.auth.setTargetEmail(email);
    setTargetEmailState(email);
  };

  // Sign in with Google - uses backend abstraction
  const signInWithGoogle = async () => {
    console.log('🔐 Signing in with Google via Appwrite backend');
    try {
      const user = await backendProvider.auth.signInWithGoogle();
        console.log('✅ Google sign-in successful');
      return user;
    } catch (error: any) {
      console.error('❌ Google sign-in failed:', error);
      throw error;
    }
  };

  // Sign in with LinkedIn - uses backend abstraction
  const signInWithLinkedIn = async () => {
    console.log('🔐 Signing in with LinkedIn via Appwrite backend');
    try {
      const user = await backendProvider.auth.signInWithLinkedIn();
      console.log('✅ LinkedIn sign-in successful');
      return user;
    } catch (error: any) {
      console.error('❌ LinkedIn sign-in failed:', error);
      throw error;
    }
  };

  // Sign out - uses backend abstraction
  const signOut = async () => {
    console.log('🔐 Signing out via Appwrite backend');
    isSigningOutRef.current = true;
    try {
      await backendProvider.auth.signOut();
      console.log('✅ Sign out successful');
    } catch (error: any) {
      console.error('❌ Sign out failed:', error);
      throw error;
    } finally {
      isSigningOutRef.current = false;
    }
  };

  // Listen for auth state changes
  useEffect(() => {
    console.log('🔐 Setting up auth state listener for Appwrite backend');

    // Use backend provider's auth state listener
    const unsubscribe = backendProvider.auth.onAuthStateChanged(async (user) => {
      console.log('🔐 Auth state changed (Appwrite):', user ? 'authenticated' : 'null');

      if (user) {
        // User resolved — cancel any pending revalidation timer and restore
        clearRevalidationTimer();
        stopRecoveryPoll(); // session recovered — no more probing needed
        lastValidUserRef.current = user;
        setCurrentUser(user);
        setLoading(false);

        // Initialize client-side preferences on first login
        try {
          await preferencesService.initializeClientPreferences();
        } catch (error: any) {
          console.error('❌ Failed to initialize preferences:', error);
          // Don't block login on preferences failure
        }
      } else {
        // Auth fired null (could be network hiccup or real sign-out)

        // If this is an explicit sign-out, clear immediately
        if (isSigningOutRef.current) {
          clearRevalidationTimer();
          stopRecoveryPoll(); // real sign-out — recovery probing must not run
          lastValidUserRef.current = null;
          setCurrentUser(null);
          setLoading(false);
          preferencesService.clearCache();
          return;
        }

        // If we've never had a valid user, this is just initial null — pass through
        if (!lastValidUserRef.current) {
          setCurrentUser(null);
          setLoading(false);
          return;
        }

        // We had a valid user — start a revalidation timer. Keep showing the
        // last known user until the timer expires (30 seconds) or the user
        // resolves. The 30-second window covers common internet dropouts
        // (metro, elevator, conference hall) without clearing the user.
        // During a dropout Appwrite's SDK can fire many null events rapidly,
        // so we DO NOT count them — we just extend the timer on each event.
        clearRevalidationTimer();
        revalidationTimerRef.current = setTimeout(() => {
          // Timer expired without user resolving — clear state.
          // The user didn't sign out; they just lost connection for a while.
          // When connection returns, the auth listener fires with the real user.
          lastValidUserRef.current = null;
          setCurrentUser(null);
          setLoading(false);
          preferencesService.clearCache();

          // Start the recovery poll: the SDK fires NO auth event on reconnect,
          // so we probe account.get() ourselves until the session resolves.
          // 10s cadence — snappy on the login screen, cheap for the server.
          // stopRecoveryPoll fires automatically when the listener sees a user.
          stopRecoveryPoll();
          recoveryPollRef.current = setInterval(probeSession, 10_000);
          probeSession(); // immediate first attempt
        }, 30000);

        // Keep the last known user visible while we wait
        setCurrentUser(lastValidUserRef.current);
        setLoading(false);
      }
    });

    return () => {
      clearRevalidationTimer();
      stopRecoveryPoll();
      unsubscribe();
    };
  }, []);

  const isSuperAdmin = currentUser?.labels?.includes('superadmin') ?? false;
  const isTeamAdmin = currentUser?.labels?.includes('teamadmin') ?? false;

  const value: AuthContextType = {
    currentUser,
    isSuperAdmin,
    isTeamAdmin,
    isAdmin: isSuperAdmin, // backwards compat — isAdmin === isSuperAdmin
    targetEmail,
    setTargetEmail,
    loading,
    signInWithGoogle,
    signInWithLinkedIn,
    signOut
  };

  if (loading) {
    // The very first paint of the app — same branded loader the routes use,
    // so the boot → route handoff never jumps between two spinner styles.
    // The fullscreen variant owns its own bg + min-h-dvh centering.
    return (
      <BrainiacsLoader
        variant="fullscreen"
        message="Connecting to Brainiacs AI Engine"
        subtitle="Checking your session and loading the latest account data."
      />
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
